import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  Button,
  Link,
  Alert,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Search, FileDownload, InfoOutlined } from '@mui/icons-material';

interface ApplicationCertificationItem {
  name: string;
  u_application_id: string;
  'u_business_platform.value': string;
  owned_by_name: string;
  managed_by_name: string | null;
  u_engineering_manager_name: string | null;
  u_sr_engineering_manager_name: string | null;
  u_engineering_director_name: string | null;
  portfolio: string;
  platform: string;
  portfolio_id: string;
  platform_id: string;
  has_server_relation: boolean;
  has_app_relation: boolean;
  has_cloud: boolean;
  is_saas: boolean;
  mac_owners: string;
  status: string;
  is_mvc: boolean;
  is_bca: boolean;
  has_repo: boolean;
  is_standalone: boolean;
  test_app: boolean;
  verified: string | null;
  verified_at: string | null;
  app_verified: string | null;
  app_verified_at: string | null;
  own_verified: string;
  own_verified_at: string;
  verified_owner: string | null;
  app_verified_owner: string | null;
  own_verified_owner: string;
  verified_expired: string | null;
  app_verified_expired: string | null;
  completeness_percentage: number;
  certification_percentage: number;
  id: number;
  overall_app_id_completeness_percentage: number;
  overall_app_id_certification_percentage: number;
}

interface ApplicationCertificationProps {
  certificationData: ApplicationCertificationItem[];
  filteredCertificationData: ApplicationCertificationItem[];
  certificationLoading: boolean;
  changeOnboardingLoading: boolean;
  certificationError: string | null;
  mvcStatusFilter: boolean;
  setMvcStatusFilter: (value: boolean) => void;
  bcaStatusFilter: boolean;
  setBcaStatusFilter: (value: boolean) => void;
  certificationSearchQuery: string;
  setCertificationSearchQuery: (value: string) => void;
  certificationView: 'detailed' | 'platform';
  setCertificationView: (value: 'detailed' | 'platform') => void;
  platformViewMvcFilter: boolean;
  setPlatformViewMvcFilter: (value: boolean) => void;
  platformViewBcaFilter: boolean;
  setPlatformViewBcaFilter: (value: boolean) => void;
  platformViewSearchQuery: string;
  setPlatformViewSearchQuery: (value: string) => void;
  uniqueAppIdsCountForCertification: number;
  totalCertificationApplications: number;
  avgCertification: number;
  avgCompleteness: number;
  uniqueAppIdsCountForCertificationPlatformView: number;
  totalCertificationApplicationsPlatformView: number;
  avgCertificationPlatformView: number;
  avgCompletenessPlatformView: number;
  platformViewData: any[];
  transformPortfolioName: (portfolio: string) => string;
  handleExportPlatformViewToCSV: () => void;
}

export const ApplicationCertification: React.FC<ApplicationCertificationProps> = ({
  filteredCertificationData,
  certificationLoading,
  changeOnboardingLoading,
  certificationError,
  mvcStatusFilter,
  setMvcStatusFilter,
  bcaStatusFilter,
  setBcaStatusFilter,
  certificationSearchQuery,
  setCertificationSearchQuery,
  certificationView,
  setCertificationView,
  platformViewMvcFilter,
  setPlatformViewMvcFilter,
  platformViewBcaFilter,
  setPlatformViewBcaFilter,
  platformViewSearchQuery,
  setPlatformViewSearchQuery,
  uniqueAppIdsCountForCertification,
  totalCertificationApplications,
  avgCertification,
  avgCompleteness,
  uniqueAppIdsCountForCertificationPlatformView,
  totalCertificationApplicationsPlatformView,
  avgCertificationPlatformView,
  avgCompletenessPlatformView,
  platformViewData,
  transformPortfolioName,
  handleExportPlatformViewToCSV,
}) => {
  const [certificationPage, setCertificationPage] = useState(0);
  const [certificationRowsPerPage, setCertificationRowsPerPage] = useState(10);
  const [certificationSortConfig, setCertificationSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [platformViewPage, setPlatformViewPage] = useState(0);
  const [platformViewRowsPerPage, setPlatformViewRowsPerPage] = useState(10);
  const [platformViewSortConfig, setPlatformViewSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const isCertificationDataLoading = certificationLoading || changeOnboardingLoading;
  const loaderMessage = certificationLoading ? 'Loading certification insights...' : 'Waiting for change onboarding feed...';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Application Certification Dashboard
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#f3e5f5', border: '1px solid #ba68c8' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  Total Apps
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                  {certificationView === 'platform' 
                    ? uniqueAppIdsCountForCertificationPlatformView 
                    : uniqueAppIdsCountForCertification}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#e8f5e9', border: '1px solid #81c784' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  Total Apps (excluding test apps)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#388e3c' }}>
                  {certificationView === 'platform' 
                    ? totalCertificationApplicationsPlatformView 
                    : totalCertificationApplications}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  Overall Certification
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#f57c00' }}>
                  {certificationView === 'platform' 
                    ? avgCertificationPlatformView 
                    : avgCertification}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#ffebee', border: '1px solid #e57373' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="textPrimary" gutterBottom>
                  Overall Completeness
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#c62828' }}>
                  {certificationView === 'platform' 
                    ? avgCompletenessPlatformView 
                    : avgCompleteness}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* View Tabs */}
        <Tabs
          value={certificationView}
          onChange={(e, newValue) => setCertificationView(newValue)}
          sx={{ 
            mb: 2,
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Tab label="Detailed View" value="detailed" />
          <Tab label="Platform View" value="platform" />
        </Tabs>

        {/* Detailed View - Filters and Search */}
        {certificationView === 'detailed' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={mvcStatusFilter}
                  onChange={(e) => {
                    setMvcStatusFilter(e.target.checked);
                    setCertificationPage(0);
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: '#efededff',
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1976d2',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#565555ff',
                    },
                  }}
                />
              }
              label="MVC"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={bcaStatusFilter}
                  onChange={(e) => {
                    setBcaStatusFilter(e.target.checked);
                    setCertificationPage(0);
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: '#efededff',
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1976d2',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#565555ff',
                    },
                  }}
                />
              }
              label="BCA"
            />

            <TextField
              size="small"
              placeholder="Search by App ID, Name, Portfolio, Platform..."
              value={certificationSearchQuery}
              onChange={(e) => {
                setCertificationSearchQuery(e.target.value);
                setCertificationPage(0);
              }}
              InputProps={{
                startAdornment: <Search sx={{ color: '#6c757d', mr: 1 }} />,
              }}
              sx={{
                flexGrow: 1,
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa',
                },
              }}
            />

            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => {
                const headers = ['Application ID', 'Application Name', 'Certification %', 'Completeness %', 'Platform', 'Portfolio'];
                const csvRows = [headers.join(',')];
                
                filteredCertificationData.forEach(item => {
                  const row = [
                    item.u_application_id || '',
                    `"${(item.name || '').replace(/"/g, '""')}"`,
                    item.certification_percentage || 0,
                    item.completeness_percentage || 0,
                    `"${(item.platform || 'Undefined').replace(/"/g, '""')}"`,
                    `"${transformPortfolioName(item.portfolio || 'Undefined').replace(/"/g, '""')}"`,
                  ];
                  csvRows.push(row.join(','));
                });
                
                const csvContent = csvRows.join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `application_certification_${new Date().toISOString().split('T')[0]}.csv`;
                link.click();
                window.URL.revokeObjectURL(url);
              }}
              sx={{
                borderColor: '#00A8CC',
                color: '#00A8CC',
                '&:hover': {
                  borderColor: '#0067b3',
                  backgroundColor: 'rgba(0, 168, 204, 0.04)',
                },
              }}
            >
              Export CSV
            </Button>
          </Box>
        )}

        {/* Platform View - Filters and Export Button */}
        {certificationView === 'platform' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={platformViewMvcFilter}
                  onChange={(e) => {
                    setPlatformViewMvcFilter(e.target.checked);
                    setPlatformViewPage(0);
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: '#efededff',
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1976d2',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#565555ff',
                    },
                  }}
                />
              }
              label="MVC"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={platformViewBcaFilter}
                  onChange={(e) => {
                    setPlatformViewBcaFilter(e.target.checked);
                    setPlatformViewPage(0);
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: '#efededff',
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1976d2',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#565555ff',
                    },
                  }}
                />
              }
              label="BCA"
            />

            <TextField
              size="small"
              placeholder="Search by Portfolio, Platform..."
              value={platformViewSearchQuery}
              onChange={(e) => {
                setPlatformViewSearchQuery(e.target.value);
                setPlatformViewPage(0);
              }}
              InputProps={{
                startAdornment: <Search sx={{ color: '#6c757d', mr: 1 }} />,
              }}
              sx={{
                flexGrow: 1,
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa',
                },
              }}
            />

            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExportPlatformViewToCSV}
              sx={{
                borderColor: '#00A8CC',
                color: '#00A8CC',
                '&:hover': {
                  borderColor: '#0067b3',
                  backgroundColor: 'rgba(0, 168, 204, 0.04)',
                },
              }}
            >
              Export CSV
            </Button>
          </Box>
        )}
      </Box>

      {isCertificationDataLoading ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed #c5c8d1',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            mb: 3,
            bgcolor: '#fafbff',
          }}
        >
          <CircularProgress size={28} sx={{ mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            {loaderMessage}
          </Typography>
        </Paper>
      ) : certificationError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {certificationError}
        </Alert>
      ) : certificationView === 'detailed' && filteredCertificationData.length > 0 ? (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> This table displays instance-level data. There may be multiple rows for a single App ID representing its different production instances.
              If you are the owner and want to certify an application, please click on the App ID hyperlink and use the certify option on MSA.
            </Typography>
          </Alert>
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>
                    <TableSortLabel
                      active={certificationSortConfig?.key === 'u_application_id'}
                      direction={certificationSortConfig?.key === 'u_application_id' ? certificationSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = certificationSortConfig?.key === 'u_application_id' && certificationSortConfig.direction === 'asc';
                        setCertificationSortConfig({ key: 'u_application_id', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Application ID
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={certificationSortConfig?.key === 'name'}
                      direction={certificationSortConfig?.key === 'name' ? certificationSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = certificationSortConfig?.key === 'name' && certificationSortConfig.direction === 'asc';
                        setCertificationSortConfig({ key: 'name', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Application Name
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <TableSortLabel
                        active={certificationSortConfig?.key === 'overall_app_id_certification_percentage'}
                        direction={certificationSortConfig?.key === 'overall_app_id_certification_percentage' ? certificationSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = certificationSortConfig?.key === 'overall_app_id_certification_percentage' && certificationSortConfig.direction === 'asc';
                          setCertificationSortConfig({ key: 'overall_app_id_certification_percentage', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          App ID Certification %
                        </Typography>
                      </TableSortLabel>
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="body2" gutterBottom>
                              Indicates if application ownership, app to app and infrastructure relationships are certified in MSA. 
                              Certification ensures that the application team has validated the existing data and can be confidently consumed.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Parameters:</strong> Ownership(50%), Infra certified, to app certified (25% each). 
                              Scores are averaged when multiple MSA Production instances are registered against a single MAC application.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0 }}>
                          <InfoOutlined sx={{ fontSize: 16, color: '#1976d2' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <TableSortLabel
                        active={certificationSortConfig?.key === 'overall_app_id_completeness_percentage'}
                        direction={certificationSortConfig?.key === 'overall_app_id_completeness_percentage' ? certificationSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = certificationSortConfig?.key === 'overall_app_id_completeness_percentage' && certificationSortConfig.direction === 'asc';
                          setCertificationSortConfig({ key: 'overall_app_id_completeness_percentage', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          App ID Completeness %
                        </Typography>
                      </TableSortLabel>
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="body2" gutterBottom>
                              Indicates if application ownership, app to app and infrastructure relationships are registered/documented in MSA.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Parameters:</strong> Ownership(50%), Infra documented, to app documented (25% each). 
                              Scores are averaged when multiple MSA Production instances are registered against a single MAC application.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0 }}>
                          <InfoOutlined sx={{ fontSize: 16, color: '#1976d2' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={certificationSortConfig?.key === 'platform'}
                      direction={certificationSortConfig?.key === 'platform' ? certificationSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = certificationSortConfig?.key === 'platform' && certificationSortConfig.direction === 'asc';
                        setCertificationSortConfig({ key: 'platform', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Platform
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={certificationSortConfig?.key === 'portfolio'}
                      direction={certificationSortConfig?.key === 'portfolio' ? certificationSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = certificationSortConfig?.key === 'portfolio' && certificationSortConfig.direction === 'asc';
                        setCertificationSortConfig({ key: 'portfolio', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Portfolio
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Sort data
                  let sortedData = [...filteredCertificationData];
                  if (certificationSortConfig) {
                    sortedData.sort((a: any, b: any) => {
                      const aVal = a[certificationSortConfig.key];
                      const bVal = b[certificationSortConfig.key];
                      const direction = certificationSortConfig.direction === 'asc' ? 1 : -1;
                      
                      if (typeof aVal === 'number' && typeof bVal === 'number') {
                        return (aVal - bVal) * direction;
                      }
                      
                      return String(aVal || '').localeCompare(String(bVal || '')) * direction;
                    });
                  }
                  
                  // Paginate
                  const startIndex = certificationPage * certificationRowsPerPage;
                  const endIndex = startIndex + certificationRowsPerPage;
                  const paginatedData = sortedData.slice(startIndex, endIndex);
                  
                  return paginatedData.map((item, index) => {
                    const appId = item.u_application_id || '';
                    const isValidAppId = appId && appId.trim() !== '' && appId.toUpperCase() !== 'N/A' && appId.toUpperCase() !== 'NA';
                    
                    return (
                      <TableRow key={item.id || index} hover>
                        <TableCell>
                          {isValidAppId ? (
                            <Link
                              href={`https://msa.maersk.io/asset_lookup?applicationId=${appId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 500, textDecoration: 'none', color: '#1976d2', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {appId}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {appId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.name || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${item.overall_app_id_certification_percentage || 0}%`}
                            size="small"
                            sx={{
                              bgcolor: Number(item.overall_app_id_certification_percentage) >= 100 ? '#c8e6c9 !important' : 
                                       Number(item.overall_app_id_certification_percentage) > 0 ? '#fff9c4 !important' : '#ffcdd2 !important',
                              color: Number(item.overall_app_id_certification_percentage) >= 100 ? '#2e7d32 !important' : 
                                     Number(item.overall_app_id_certification_percentage) > 0 ? '#f57f17 !important' : '#c62828 !important',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${item.overall_app_id_completeness_percentage || 0}%`}
                            size="small"
                            sx={{
                              bgcolor: Number(item.overall_app_id_completeness_percentage) >= 100 ? '#c8e6c9 !important' : 
                                       Number(item.overall_app_id_completeness_percentage) > 0 ? '#fff9c4 !important' : '#ffcdd2 !important',
                              color: Number(item.overall_app_id_completeness_percentage) >= 100 ? '#2e7d32 !important' : 
                                     Number(item.overall_app_id_completeness_percentage) > 0 ? '#f57f17 !important' : '#c62828 !important',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.platform || 'Undefined'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transformPortfolioName(item.portfolio || 'Undefined')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredCertificationData.length}
            page={certificationPage}
            onPageChange={(e, newPage) => setCertificationPage(newPage)}
            rowsPerPage={certificationRowsPerPage}
            onRowsPerPageChange={(e) => {
              setCertificationRowsPerPage(parseInt(e.target.value, 10));
              setCertificationPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      ) : certificationView === 'platform' && platformViewData.length > 0 ? (
        <Box>
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'platform'}
                      direction={platformViewSortConfig?.key === 'platform' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'platform' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'platform', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Platform
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'portfolio'}
                      direction={platformViewSortConfig?.key === 'portfolio' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'portfolio' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'portfolio', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Portfolio
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'totalApps'}
                      direction={platformViewSortConfig?.key === 'totalApps' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'totalApps' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'totalApps', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        No. of App IDs
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'fullyCertified'}
                      direction={platformViewSortConfig?.key === 'fullyCertified' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'fullyCertified' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'fullyCertified', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Fully Certified
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'partiallyCertified'}
                      direction={platformViewSortConfig?.key === 'partiallyCertified' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'partiallyCertified' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'partiallyCertified', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Partially Certified
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'notCertified'}
                      direction={platformViewSortConfig?.key === 'notCertified' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'notCertified' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'notCertified', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Not Certified
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'overallCertification'}
                      direction={platformViewSortConfig?.key === 'overallCertification' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'overallCertification' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'overallCertification', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Overall Certification %
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={platformViewSortConfig?.key === 'overallCompleteness'}
                      direction={platformViewSortConfig?.key === 'overallCompleteness' ? platformViewSortConfig.direction : 'asc'}
                      onClick={() => {
                        const isAsc = platformViewSortConfig?.key === 'overallCompleteness' && platformViewSortConfig.direction === 'asc';
                        setPlatformViewSortConfig({ key: 'overallCompleteness', direction: isAsc ? 'desc' : 'asc' });
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Overall Completeness %
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Sort platform view data
                  let sortedData = [...platformViewData];
                  if (platformViewSortConfig) {
                    sortedData.sort((a: any, b: any) => {
                      const aVal = a[platformViewSortConfig.key];
                      const bVal = b[platformViewSortConfig.key];
                      const direction = platformViewSortConfig.direction === 'asc' ? 1 : -1;
                      
                      if (typeof aVal === 'number' && typeof bVal === 'number') {
                        return (aVal - bVal) * direction;
                      }
                      
                      return String(aVal || '').localeCompare(String(bVal || '')) * direction;
                    });
                  }
                  
                  // Paginate platform view data
                  const startIndex = platformViewPage * platformViewRowsPerPage;
                  const endIndex = startIndex + platformViewRowsPerPage;
                  const paginatedData = sortedData.slice(startIndex, endIndex);
                  
                  return paginatedData.map((platform, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {platform.platform || 'Undefined'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transformPortfolioName(platform.portfolio || 'Undefined')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {platform.totalApps}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {platform.fullyCertified}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {platform.partiallyCertified}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {platform.notCertified}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${platform.overallCertification}%`}
                          size="small"
                          sx={{
                            bgcolor: platform.overallCertification >= 100 ? '#c8e6c9 !important' : 
                                     platform.overallCertification > 0 ? '#fff9c4 !important' : '#ffcdd2 !important',
                            color: platform.overallCertification >= 100 ? '#2e7d32 !important' : 
                                   platform.overallCertification > 0 ? '#f57f17 !important' : '#c62828 !important',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${platform.overallCompleteness}%`}
                          size="small"
                          sx={{
                            bgcolor: platform.overallCompleteness >= 100 ? '#c8e6c9 !important' : 
                                     platform.overallCompleteness > 0 ? '#fff9c4 !important' : '#ffcdd2 !important',
                            color: platform.overallCompleteness >= 100 ? '#2e7d32 !important' : 
                                   platform.overallCompleteness > 0 ? '#f57f17 !important' : '#c62828 !important',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={platformViewData.length}
            page={platformViewPage}
            onPageChange={(e, newPage) => setPlatformViewPage(newPage)}
            rowsPerPage={platformViewRowsPerPage}
            onRowsPerPageChange={(e) => {
              setPlatformViewRowsPerPage(parseInt(e.target.value, 10));
              setPlatformViewPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No certification data available
          </Typography>
        </Box>
      )}
    </Box>
  );
};
